import { useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { markShareDoorDone } from "../../lib/api";
import { qk } from "../../lib/queries";
import { chipClass } from "../ui/button";
import { CopyButton } from "../ui/copy-button";
import { SparkIcon } from "../ui/icons";
import { canonicalUrl } from "../you/door-page";
import { Sheet } from "./surfaces";
import { useShellUi } from "./state";
import { useMetered } from "./use-metered";

export const SHARE_TITLE = "Hand out your door";
export const SHARE_LEDE =
  "A door nobody has is a door nobody knocks on. Put it where people already look for you.";
export const SNIPPETS_HEADING = "Ready to paste";

/**
 * THE FIFTH SNIPPET, WRITTEN TO ORDER.
 *
 * The four above it are finished sentences, and finished sentences in somebody
 * else's voice are exactly what a lot of people will not post. The doorman has
 * read their rules and knows how they talk, so the honest answer to "none of
 * these sound like me" is to ask it — which is an ordinary metered turn in the
 * ordinary thread, not a new kind of object with a place to keep it.
 */
export const WRITE_ONE_LABEL = "Ask Nob to write you one";
export const WRITE_ONE_LINE = "None of these sound like you? Your doorman knows how you talk.";
export const WRITE_ONE_ASK =
  "Write me a short post handing out my door. Use my house rules to make it sound like me, " +
  "and give me one for X and one for LinkedIn.";
/** What Free is offered instead: the thing it lacks, not the thing it cannot do. */
export const WRITE_ONE_LOCKED = "Writing one takes a plan — Free holds the door, it does not talk.";
export const WRITE_ONE_UPGRADE = "See plans";

/**
 * Finished sentences, not a URL.
 *
 * A raw address is the least useful thing to hand somebody: it leaves them
 * writing the sentence around it, and most people do not, and the link goes in
 * a note app instead of a bio. Each block below is the whole text for one
 * place, so sharing is a copy and a paste rather than a small writing task.
 *
 * Approved Part 3a, built here for the first time. The set is the owner's
 * prototype's, reworded once: it said "Nob reads what you send" in the first
 * person plural, and this product's voice does not speak for the doorman.
 *
 * THE ORDER IS THE ORDER PEOPLE ACT IN (owner, 2026-08-27). The bio line is the
 * one-second job, the two posts are the ones that actually put the door in front
 * of strangers, and the DM reply and the signature are the slow drip underneath.
 * The posts used to sit below the DM, which buried the only two blocks with a
 * publish button attached.
 */
export function shareSnippets(
  url: string,
  name: string,
): {
  id: string;
  label: string;
  hint: string;
  text: string;
  post?: "x" | "linkedin";
}[] {
  return [
    {
      id: "bio",
      label: "Bio line",
      hint: "X, Instagram, anywhere with one line",
      text: `Pitch me at ${url}`,
    },
    {
      id: "x",
      label: "X post",
      hint: "Pinned, ideally",
      post: "x",
      text: `I put a doorman on my inbox. It reads what you send, asks the questions I would ask, and only wakes me for the ones worth it.\n\nKnock here: ${url}`,
    },
    {
      id: "linkedin",
      label: "LinkedIn post",
      hint: "Same idea, longer room",
      post: "linkedin",
      // FIRST PERSON, NOT THIRD (owner, 2026-08-27). It used to say "if you want
      // to reach <firstname>" — a post written in the first person that turns and
      // refers to its own author by name in the last line reads as ghostwritten,
      // which is the one thing that stops somebody posting it.
      text: `My inbox stopped scaling, so I gave it a doorman.\n\nIt reads every message that arrives, asks for the missing context before I ever see it, and turns away anything with no real ask. Nothing is deleted, it just waits until it makes sense.\n\nIf you want to reach me, knock here: ${url}`,
    },
    {
      id: "dm",
      label: "Reply to a cold DM",
      hint: "Kinder than silence",
      text: `Best way to reach me is ${url} — my doorman reads it and asks the questions I would.`,
    },
    {
      id: "signature",
      label: "Email signature",
      hint: "Under your name",
      text: `${name}\nBest way to reach me: ${url}`,
    },
  ];
}

/** The label on the primary action of a post block — where it actually goes. */
export function postLabel(post: "x" | "linkedin"): string {
  return post === "x" ? "Post on X" : "Post on LinkedIn";
}

export const COPY_POST_LABEL = "Copy the post";

/**
 * Where a finished post goes to be published, with the text already in it.
 *
 * LINKEDIN HAS EXACTLY ONE ENDPOINT THAT PREFILLS TEXT, and this is it. The
 * documented `sharing/share-offsite/?url=` takes a URL and nothing else — it
 * builds the card from the target page's Open Graph tags and leaves the
 * commentary box empty — and `shareArticle`'s title/summary parameters are
 * deprecated and ignored. These snippets ARE the text, so the feed composer is
 * the only one that can be handed them.
 */
export function composeUrl(post: "x" | "linkedin", text: string): string {
  if (post === "x") return `https://x.com/intent/post?text=${encodeURIComponent(text)}`;
  return `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
}

/**
 * The one place a door gets handed out.
 *
 * NO STATS AND NO RULES POSTER (owner, 2026-08-25). Both used to close this
 * sheet: "1,247 knocks · 12 got a reply" and a share card of the House Rules.
 * The stats line is an argument for the product made out of a number that, for
 * the person most in need of this sheet, reads "0 knocks" — a door with nobody
 * at it, printed under a heading inviting them to advertise it. And the rules
 * poster is a second thing to share at the moment somebody has decided to share
 * the first; a sheet that offers two answers to one question gets neither
 * pressed. What is left is the address, five ways to say it, and the way to get
 * a sixth written for you.
 */
export function ShareDoorSheet({ handle, name }: { handle: string; name: string }) {
  const ui = useShellUi();
  const queryClient = useQueryClient();
  const metered = useMetered() === true;
  const url = canonicalUrl(handle);
  const blocks = shareSnippets(url, name || `@${handle}`);

  /**
   * The doorman's second standing prompt (FR-11) is answered HERE, because this
   * is the only surface where handing the door out actually happens — in a
   * clipboard or in a composer, where the server can see nothing. Once per
   * sheet, however many blocks get used, and only after something really
   * happened: a failed report is not claimed, and the next one may make it
   * again. A denied clipboard reports nothing, because it copied nothing.
   */
  const reported = useRef(false);
  const reportShared = useCallback(() => {
    if (reported.current) return;
    reported.current = true;
    void markShareDoorDone().then(async (r) => {
      if (!r.ok) {
        reported.current = false;
        return;
      }
      await queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    });
  }, [queryClient]);

  return (
    <Sheet title={SHARE_TITLE} lede={SHARE_LEDE} onClose={ui.closeSheet} wide>
      <div className="flex items-center justify-between gap-3 rounded-md bg-raised px-3 py-2.5">
        <span className="truncate font-mono text-sm font-medium">{url}</span>
        <CopyButton value={url} label="Copy" onCopied={reportShared} />
      </div>

      <h3 className="mt-5 mb-2 text-[11px] font-medium uppercase tracking-[0.12em] text-faint">
        {SNIPPETS_HEADING}
      </h3>
      <ul className="flex flex-col gap-2">
        {blocks.map((block) => (
          <li key={block.id} className="rounded-md border border-border-soft bg-surface p-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-semibold">{block.label}</p>
              <p className="text-xs text-faint">{block.hint}</p>
            </div>
            <p className="mt-1.5 text-sm leading-snug whitespace-pre-line text-muted">
              {block.text}
            </p>
            {/*
             * PUBLISHING LEADS WHERE PUBLISHING IS POSSIBLE (owner, 2026-08-27).
             * A block with a composer behind it has one obvious action — go and
             * post it — and the clipboard is the fallback for somebody who wants
             * to edit first. Everywhere else there is nowhere to send the text,
             * so the copy IS the action and keeps the chip.
             */}
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {block.post ? (
                <>
                  <a
                    href={composeUrl(block.post, block.text)}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={reportShared}
                    className={chipClass}
                  >
                    {postLabel(block.post)}
                  </a>
                  <CopyButton
                    value={block.text}
                    label={COPY_POST_LABEL}
                    variant="quiet"
                    onCopied={reportShared}
                  />
                </>
              ) : (
                <CopyButton
                  value={block.text}
                  label={`Copy the ${block.label.toLowerCase()}`}
                  onCopied={reportShared}
                />
              )}
            </div>
          </li>
        ))}
      </ul>

      <WriteOne
        metered={metered}
        onAsk={() => {
          // THE SIXTH SNIPPET COUNTS TOO (owner, 2026-08-27). Every other way out
          // of this sheet answers the doorman's prompt; this one used not to, so
          // the person who took the most deliberate route — asking for a post in
          // their own voice — was the one still being nagged to hand their door
          // out. The prompt asks whether the sheet was acted on, and it was.
          reportShared();
          ui.closeSheet();
          ui.askDoorman(WRITE_ONE_ASK, true);
        }}
        onSeePlans={() => {
          ui.closeSheet();
          ui.openBilling("plans");
        }}
      />
    </Sheet>
  );
}

/**
 * The sixth snippet's offer, and its refusal.
 *
 * DISABLED RATHER THAN ABSENT ON FREE. Hiding it would leave a Free owner with
 * no way to learn that the doorman writes — and the whole argument for a plan is
 * that it does work like this. So the button is drawn, plainly out of reach, and
 * the sentence under it says which of the two things Free is: it holds the door,
 * and it does not talk. The nudge beside it is the only road out, and it goes to
 * the plans rather than to the panel index, because a plan is what is missing.
 */
function WriteOne({
  metered,
  onAsk,
  onSeePlans,
}: {
  metered: boolean;
  onAsk: () => void;
  onSeePlans: () => void;
}) {
  return (
    <div className="mt-6 border-t border-border-soft pt-5">
      <button
        type="button"
        disabled={!metered}
        onClick={onAsk}
        className="nob-press flex w-full items-center justify-center gap-2 rounded-full border
                   border-border-strong px-4 py-2.5 text-sm font-semibold transition-colors
                   duration-75 hover:border-border-loud hover:bg-surface
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70
                   disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent"
      >
        <SparkIcon className="h-4 w-4" />
        {WRITE_ONE_LABEL}
      </button>
      {metered ? (
        <p className="mt-2 text-center text-xs leading-snug text-muted">{WRITE_ONE_LINE}</p>
      ) : (
        <p className="mt-2 text-center text-xs leading-snug text-muted">
          {WRITE_ONE_LOCKED}{" "}
          <button
            type="button"
            onClick={onSeePlans}
            className="font-medium text-text underline underline-offset-4 transition-colors
                       duration-75 hover:text-accent-ink-strong focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            {WRITE_ONE_UPGRADE}
          </button>
        </p>
      )}
    </div>
  );
}
